
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Product } from '@/lib/supabase/products';
import { clearOrganizationCache } from '@/lib/supabase/products/organization';
import { fetchProductByUUID } from './fetchers/fetchProductByUUID';
import { fetchProductByShop } from './fetchers/fetchProductByShop';
import { fetchProductFromAllProducts } from './fetchers/fetchProductFromAllProducts';

export const useProductFetch = (
  productIdOrSlug?: string,
  shopId?: string | null,
  organizationId?: string | null,
  setOrganizationId?: (id: string | null) => void
) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productIdOrSlug) {
        console.log("No product ID or slug provided");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        console.log("Attempting to fetch product with parameters:", {
          productIdOrSlug, 
          shopId, 
          organizationId
        });
        
        // Strategy 1: Direct UUID lookup if the ID looks like a UUID
        if (productIdOrSlug.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const productData = await fetchProductByUUID(
            productIdOrSlug, 
            shopId, 
            organizationId, 
            setOrganizationId
          );
          
          if (productData) {
            setProduct(productData);
            setLoading(false);
            return;
          }
        }
        
        // Strategy 2: Find product in specific shop
        if (shopId) {
          const productData = await fetchProductByShop(
            productIdOrSlug, 
            shopId, 
            organizationId, 
            setOrganizationId
          );
          
          if (productData) {
            setProduct(productData);
            setLoading(false);
            return;
          }
        }
        
        // Strategy 3: Find product in all products
        const productData = await fetchProductFromAllProducts(
          productIdOrSlug, 
          organizationId, 
          setOrganizationId
        );
        
        if (productData) {
          setProduct(productData);
          setLoading(false);
          return;
        }
        
        // If we get here, product wasn't found
        console.error("Product not found with identifier:", productIdOrSlug);
        toast({
          title: "Produit non trouvé",
          description: "Le produit demandé n'existe pas ou n'est pas disponible",
          variant: "destructive",
        });
      } catch (error) {
        console.error("Error fetching product by ID:", error);
        toast({
          title: "Erreur",
          description: "Impossible de récupérer les informations du produit",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    // Reset state when parameters change
    setProduct(null);
    
    // Clear organization cache to ensure fresh data
    if (!organizationId) {
      clearOrganizationCache();
    }
    
    fetchProduct();
  }, [productIdOrSlug, shopId, organizationId, setOrganizationId, toast]);

  return { product, loading };
};
